import { useEffect, useMemo, useState } from 'react';
import { employeesApi } from '@/api/employees.api';
import type { SelectOption } from '@/components/ui/Select';
import type { EmployeeResponse } from '@/types/employee';
import { formatEmployeeLabel, formatEmployeeShort } from '@/utils/entityLabels';
import { showApiError } from '@/utils/apiError';

type UseEmployeeOptionsParams = {
  activeOnly?: boolean;
  pageSize?: number;
};

export function useEmployeeOptions(params: UseEmployeeOptionsParams = {}) {
  const { activeOnly = true, pageSize = 500 } = params;
  const [employees, setEmployees] = useState<EmployeeResponse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    employeesApi
      .list({ page: 0, size: pageSize, activeOnly })
      .then((res) => setEmployees(res.content))
      .catch((err) => showApiError(err, 'Failed to load employees'))
      .finally(() => setLoading(false));
  }, [activeOnly, pageSize]);

  const options: SelectOption[] = useMemo(
    () =>
      employees.map((employee) => ({
        value: String(employee.id),
        label: formatEmployeeLabel(employee),
      })),
    [employees],
  );

  const byId = useMemo(() => new Map(employees.map((e) => [e.id, e])), [employees]);

  const getEmployee = (id: number | string | null | undefined) => {
    if (id == null || id === '') return undefined;
    return byId.get(Number(id));
  };

  const getLabel = (id: number | string | null | undefined, short = false) => {
    const employee = getEmployee(id);
    if (!employee) return id != null && id !== '' ? `#${id}` : '—';
    return short ? formatEmployeeShort(employee) : formatEmployeeLabel(employee);
  };

  return { employees, options, loading, getEmployee, getLabel, byId };
}
